import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";
import { api, pick, waLink } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Reveal, FadeIn, MaskedLine, Eyebrow } from "../components/Reveal";

const fmt = (n) => `Rs ${Number(n).toLocaleString("en-MU")}`;

export default function ServicePage() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const { data: services = [], isLoading } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then(r => r.data) });
  const s = services.find(x => x.slug === slug);

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center text-forest-700 pt-16" data-testid="service-loading">{t.common.loading}</div>;
  }
  if (!s) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 pt-16 text-center px-6" data-testid="service-not-found">
        <p className="text-forest-900 font-display text-2xl font-semibold">404</p>
        <Link to="/" className="inline-flex items-center gap-2 font-semibold text-forest-700 hover:text-forest-900" data-testid="service-back-home"><ArrowLeft size={16} /> {t.nav.home}</Link>
      </div>
    );
  }

  const pkgCards = [
    { key: "full", title: t.booking.full, desc: t.booking.fullDesc, price: s.full_price, dark: true, q: "package=full" },
    { key: "half", title: t.booking.half, desc: t.booking.halfDesc, price: s.half_price, dark: false, q: "package=half" },
  ];

  return (
    <div className="pt-[72px]" data-testid={`service-page-${s.slug}`}>
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-16">
        <FadeIn>
          <Link to="/#services" data-testid="service-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-forest-700 hover:text-forest-900 transition-colors">
            <ArrowLeft size={16} /> {t.nav.services}
          </Link>
        </FadeIn>
        <div className="mt-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <FadeIn delay={0.05}>
              <div className="inline-flex items-center gap-2 rounded-full border border-forest-200 bg-white/80 px-4 py-2 text-xs font-semibold text-forest-800">
                <ShieldCheck size={14} className="text-forest-600" /> {t.hero.badge} · BRN C22188258
              </div>
            </FadeIn>
            <h1 className="mt-6 font-display tracking-tighter text-4xl sm:text-6xl leading-[0.98]">
              <MaskedLine className="font-extrabold text-forest-950">{pick(s, "name", lang)}</MaskedLine>
              <MaskedLine delay={0.15} className="font-light text-forest-700 text-2xl sm:text-3xl mt-3">{pick(s, "tagline", lang)}</MaskedLine>
            </h1>
            <FadeIn delay={0.35}>
              <p className="mt-7 text-body text-lg leading-relaxed max-w-xl">{pick(s, "desc", lang)}</p>
            </FadeIn>
            <FadeIn delay={0.45}>
              <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3.5">
                {(lang === "fr" ? s.features_fr : s.features_en).map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm font-medium text-forest-900" data-testid={`service-feature-${i}`}>
                    <Check size={17} className="mt-0.5 shrink-0 text-forest-600" strokeWidth={3} /> {f}
                  </li>
                ))}
              </ul>
            </FadeIn>
          </div>
          <div className="lg:col-span-5">
            <FadeIn delay={0.3}>
              <div className="relative overflow-hidden rounded-[32px] shadow-2xl shadow-forest-900/15">
                <img src={s.image} alt={pick(s, "name", lang)} className="w-full h-[360px] md:h-[460px] object-cover" data-testid="service-image" />
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-2xl bg-white/90 backdrop-blur-xl px-5 py-3.5" data-testid="service-price-chip">
                  <span className="text-xs font-semibold text-forest-800">{t.services.from}</span>
                  <span className="font-display font-bold text-forest-900 text-lg">{fmt(s.half_price)} – {fmt(s.full_price)}</span>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="service-packages">
        <Reveal><Eyebrow>{t.compare.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-semibold text-forest-900">{t.booking.choosePackage}</h2>
        </Reveal>
        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {pkgCards.map((c, i) => (
            <Reveal key={c.key} delay={0.08 * i}>
              <div className={`rounded-[28px] p-9 h-full flex flex-col ${c.dark ? "bg-forest-700 text-white shadow-xl shadow-forest-700/25" : "bg-white border border-forest-100"}`} data-testid={`pkg-card-${c.key}`}>
                <div className="flex items-center gap-2.5">
                  {c.dark ? <Sparkles size={19} className="text-forest-200" /> : <CheckCircle2 size={19} className="text-forest-500" />}
                  <h3 className="font-display text-2xl font-semibold tracking-tight">{c.title}</h3>
                </div>
                <div className="mt-5 flex items-baseline gap-2">
                  <span className="font-display text-5xl font-bold tracking-tight">{fmt(c.price)}</span>
                  <span className={`text-sm ${c.dark ? "text-forest-100/85" : "text-muted-foreground"}`}>{pick(s, "unit", lang)}</span>
                </div>
                <p className={`mt-3 text-sm leading-relaxed flex-1 ${c.dark ? "text-forest-100/90" : "text-body"}`}>{c.desc}</p>
                <div className="mt-6">
                  <Link to={`/booking?service=${s.slug}&${c.q}`} data-testid={`book-${c.key}-${s.slug}`} className={`group inline-flex w-full items-center justify-center gap-2 rounded-full px-7 py-3.5 font-semibold transition-all duration-300 hover:-translate-y-0.5 ${c.dark ? "bg-white text-forest-900 hover:bg-forest-50" : "bg-forest-700 text-white hover:bg-forest-800"}`}>
                    {t.nav.book} <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <p className="mt-6 text-sm text-muted-foreground">
            {lang === "fr"
              ? "Prix indicatifs pour un travail standard — demandez un devis gratuit pour tout besoin sur mesure."
              : "Prices are indicative for standard jobs — request a free quote for anything custom."}
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24" data-testid="other-services">
        <Reveal><h3 className="font-display text-xl font-semibold text-forest-900">{t.nav.services}</h3></Reveal>
        <div className="mt-5 flex flex-wrap gap-3">
          {services.filter(x => x.slug !== s.slug).map((o, i) => (
            <Reveal key={o.slug} delay={0.05 * i}>
              <Link to={`/services/${o.slug}`} data-testid={`other-service-${o.slug}`} className="inline-flex items-center gap-2 rounded-full border border-forest-150 border-forest-200 px-5 py-2.5 text-sm font-semibold text-forest-800 hover:border-forest-500 hover:bg-mint transition-all duration-300">
                {pick(o, "name", lang)}
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}